import React, { useMemo, useState } from "react";
import {
  CalendarIcon,
  Send,
  Sparkles,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Badge } from "../components/ui/badge";

import { useApp } from "../context/AppContext";
import { bookingErrorMessage } from "../lib/bookingErrors";
import { useBookingRequest } from "../hooks/useBookingRequest";
import {
  EVENT_TYPES,
  EVENT_FASCIA,
  EVENT_SETUP,
} from "../lib/i18n";

function ChipGrid({ values, options, onToggle, multi = false, testIdPrefix }) {
  const isActive = (v) => (multi ? values.includes(v) : values === v);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = isActive(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            aria-pressed={active}
            data-testid={`${testIdPrefix}-${opt.value}`}
            className={[
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-all",
              active
                ? "border-primary bg-primary text-primary-foreground shadow-[0_0_0_4px_hsl(var(--primary)/0.18)]"
                : "border-border bg-background/70 text-foreground hover:border-primary/60 hover:bg-primary/10 hover:text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function EventForm({ user, onSubmitted, onBack }) {
  const { t, lang } = useApp();
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 12);
    return d;
  }, []);

  const [eventType, setEventType] = useState("");
  const [eventTypeOther, setEventTypeOther] = useState("");
  const [guests, setGuests] = useState(20);
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [fascia, setFascia] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [setupOptions, setSetupOptions] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const sendBooking = useBookingRequest();

  const formatDate = (d) =>
    d
      ? d.toLocaleDateString("it-IT", {
          weekday: "short",
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";

  const toISO = (d) => {
    if (!d) return "";
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 10);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    if (!eventType) {
      setError(t("event_err_type"));
      return;
    }
    if (!date || !fascia) {
      setError(t(!date ? "field_date_required" : "event_err_fascia"));
      return;
    }
    if (description.trim().length < 10) {
      setError(t("event_err_description"));
      return;
    }
    try {
      setSubmitting(true);
      const booking = await sendBooking({
        nome: user.nome,
        cognome: user.cognome || "",
        instagram: user.instagram || "",
        email: user.email,
        phone: user.phone,
        service: "event",
        date: toISO(date),
        time: "",
        guests,
        note: "",
        event_type: eventType,
        event_type_other: eventType === "other" ? eventTypeOther : "",
        event_fascia: fascia,
        event_description: description.trim(),
        event_budget: budget ? Number(budget) : null,
        event_setup: setupOptions,
      });
      onSubmitted(booking);
    } catch (err) {
      setError(bookingErrorMessage(err, lang));
    } finally {
      setSubmitting(false);
    }
  };

  const typeOpts = EVENT_TYPES.map((v) => ({ value: v, label: t(`event_type_${v}`) }));
  const fasciaOpts = EVENT_FASCIA.map((v) => ({ value: v, label: t(`fascia_${v}`) }));
  const setupOpts = EVENT_SETUP.map((v) => ({ value: v, label: t(`setup_${v}`) }));

  return (
    <div className="mx-auto max-w-2xl animate-fade-slide-up" data-testid="event-form">
      <Card className="mb-4 border-border bg-card">
        <CardContent className="flex items-center justify-between gap-3 p-4 sm:p-5">
          <div>
            <h2 className="font-display text-lg font-semibold sm:text-xl">
              {t("hello")}, {user.nome}!
            </h2>
            <Badge variant="outline" className="mt-1.5 border-primary/50 text-primary">
              <Sparkles className="mr-1 h-3 w-3" />
              {t("event_admin_label")}
            </Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="back" data-testid="event-back-button">←</Button>
        </CardContent>
      </Card>

      <Card className="border-border bg-card shadow-sm">
        <CardContent className="space-y-6 p-5 sm:p-8">
          <div>
            <h3 className="font-display text-lg font-semibold">{t("event_step_title")}</h3>
            <p className="text-sm text-muted-foreground">{t("event_step_subtitle")}</p>
          </div>

          <form onSubmit={submit} className="space-y-6">
            {/* Event type */}
            <div className="space-y-2">
              <Label>
                {t("event_field_type")} <span className="text-primary">*</span>
              </Label>
              <ChipGrid
                values={eventType}
                options={typeOpts}
                onToggle={(v) => setEventType(eventType === v ? "" : v)}
                testIdPrefix="event-type"
              />
              {eventType === "other" && (
                <Input
                  placeholder={t("event_field_type_other")}
                  value={eventTypeOther}
                  maxLength={80}
                  onChange={(e) => setEventTypeOther(e.target.value)}
                  data-testid="event-type-other-input"
                />
              )}
            </div>

            {/* Guests slider */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                {t("event_field_guests")} <span className="text-primary">*</span>
              </Label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={1}
                  max={40}
                  value={guests}
                  onChange={(e) => setGuests(Number(e.target.value))}
                  data-testid="event-guests-slider"
                  className="flex-1 accent-primary"
                />
                <div className="min-w-[3.5rem] text-right font-display text-2xl font-semibold tabular-nums text-primary">
                  {guests}
                </div>
              </div>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>
                {t("event_field_date")} <span className="text-primary">*</span>
              </Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={`h-11 w-full justify-start gap-2 border-input bg-background/70 font-normal ${
                      !date ? "text-muted-foreground" : "text-foreground"
                    }`}
                    data-testid="event-date-input"
                  >
                    <CalendarIcon className="h-4 w-4 text-primary" />
                    {date ? formatDate(date) : t("date_placeholder")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { setDate(d); setDateOpen(false); }}
                    disabled={(d) => d < today || d > maxDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Fascia */}
            <div className="space-y-2">
              <Label>
                {t("event_field_fascia")} <span className="text-primary">*</span>
              </Label>
              <ChipGrid
                values={fascia}
                options={fasciaOpts}
                onToggle={(v) => setFascia(fascia === v ? "" : v)}
                testIdPrefix="event-fascia"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="event-description">
                {t("event_field_description")} <span className="text-primary">*</span>
              </Label>
              <Textarea
                id="event-description"
                value={description}
                maxLength={800}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("event_description_placeholder")}
                rows={5}
                data-testid="event-description-input"
                autoComplete="off"
              />
              <p className="text-right text-xs text-muted-foreground">
                {800 - description.length} {t("chars_left")}
              </p>
            </div>

            {/* Setup options */}
            <div className="space-y-2">
              <Label>{t("event_field_setup")}</Label>
              <ChipGrid
                values={setupOptions}
                options={setupOpts}
                multi
                onToggle={(v) =>
                  setSetupOptions((curr) =>
                    curr.includes(v) ? curr.filter((x) => x !== v) : [...curr, v]
                  )
                }
                testIdPrefix="event-setup"
              />
            </div>

            {/* Budget (optional) */}
            <div className="space-y-2">
              <Label htmlFor="event-budget">{t("event_field_budget")}</Label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  €
                </span>
                <Input
                  id="event-budget"
                  type="number"
                  min={0}
                  max={10000}
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className="pl-7"
                  placeholder="80"
                  data-testid="event-budget-input"
                  inputMode="numeric"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive" role="alert" data-testid="event-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="min-h-12 h-auto w-full gap-2 whitespace-normal rounded-full bg-primary px-4 py-3 text-primary-foreground hover:bg-primary/90"
              disabled={submitting}
              data-testid="event-submit-button"
            >
              <Send className="h-4 w-4" />
              {t(submitting ? "submitting_request" : "event_submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
